import React, { Component } from "react";
import CartContext from "../StoreManager/CartContext";
import {
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";

export default class HotelHousekeepingTask extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      tasks: [],
      rooms: [],
      showModal: false,
      editItem: null,
      loading: false,
      saving: false,
      error: null,
      newItem: this.emptyNewItem(),
    };
  }

  emptyNewItem() {
    return {
      storeId: this.context?.storeId || "",
      roomId: "",
      status: "Dirty",
      priority: 1,
      notes: "",
      completedAt: null,
    };
  }

  componentDidMount() {
    this.loadAllData();
  }

  apiUrl(path) {
    const base = `${process.env.REACT_APP_API}${path}`;
    const storeId = this.context?.storeId;
    return storeId ? `${base}?storeId=${storeId}` : base;
  }

  fetchWithAuth(url, opts = {}) {
    const headers = opts.headers ? { ...opts.headers } : {};
    const token = this.context?.authToken;
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(url, { ...opts, headers });
  }

  async loadAllData() {
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not available in context." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const [taskRes, roomRes] = await Promise.all([
        this.fetchWithAuth(this.apiUrl("HotelHousekeepingTask")),
        this.fetchWithAuth(this.apiUrl("HotelRoom")),
      ]);

      if (!taskRes.ok || !roomRes.ok) {
        const txts = await Promise.all([
          taskRes.ok ? null : taskRes.text().catch(() => null),
          roomRes.ok ? null : roomRes.text().catch(() => null),
        ]);
        throw new Error(txts.filter(Boolean).join(" | ") || "Failed to fetch");
      }

      const [tasks, rooms] = await Promise.all([
        taskRes.json(),
        roomRes.json(),
      ]);
      this.setState({
        tasks,
        rooms,
      });
    } catch (err) {
      console.error("loadAllData error", err);
      this.setState({ error: err.message || "Failed to load data" });
    } finally {
      this.setState({ loading: false });
    }
  }

  handleShowModal = (item = null) => {
    const storeId = this.context?.storeId || "";
    if (item) {
      this.setState({
        showModal: true,
        editItem: item,
        newItem: {
          storeId,
          roomId: item.roomId,
          status: item.status,
          priority: item.priority,
          notes: item.notes || "",
          completedAt: item.completedAt || null,
        },
        error: null,
      });
    } else {
      this.setState({
        showModal: true,
        editItem: null,
        newItem: {
          ...this.emptyNewItem(),
          storeId,
        },
        error: null,
      });
    }
  };

  handleCloseModal = () =>
    this.setState({
      showModal: false,
      editItem: null,
      newItem: this.emptyNewItem(),
      error: null,
    });

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState((prev) => ({
      newItem: {
        ...prev.newItem,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  // Create or Update
  handleSave = async () => {
    const { newItem, editItem } = this.state;
    const storeId = this.context?.storeId;
    if (!storeId) return this.setState({ error: "StoreId missing." });

    if (!newItem.roomId) return this.setState({ error: "Room is required." });
    if (!newItem.status) return this.setState({ error: "Status is required." });

    const urlBase = `${process.env.REACT_APP_API}HotelHousekeepingTask`;
    const url = editItem
      ? `${urlBase}/${editItem.housekeepingTaskId}?storeId=${storeId}`
      : `${urlBase}?storeId=${storeId}`;
    const method = editItem ? "PUT" : "POST";

    // Build DTO expected by your controller
    const payload = editItem
      ? {
          status: newItem.status,
          priority: parseInt(newItem.priority, 10) || 1,
          notes: newItem.notes || null,
          markCompleted: newItem.completedAt
            ? true
            : newItem.status === "Completed",
        }
      : {
          storeId,
          roomId: newItem.roomId,
          status: newItem.status,
          priority: parseInt(newItem.priority, 10) || 1,
          notes: newItem.notes || null,
        };

    this.setState({ saving: true, error: null });
    try {
      const res = await this.fetchWithAuth(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => res.statusText);
        throw new Error(`${res.status} ${txt}`);
      }

      this.handleCloseModal();
      await this.loadAllData();
    } catch (err) {
      console.error("Error saving housekeeping task", err);
      this.setState({ error: err.message || "Save failed" });
    } finally {
      this.setState({ saving: false });
    }
  };

  handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    const storeId = this.context?.storeId;
    if (!storeId) return this.setState({ error: "StoreId missing." });

    try {
      const res = await this.fetchWithAuth(
        `${process.env.REACT_APP_API}HotelHousekeepingTask/${id}?storeId=${storeId}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => res.statusText);
        throw new Error(`${res.status} ${txt}`);
      }
      await this.loadAllData();
    } catch (err) {
      console.error("Error deleting task", err);
      this.setState({ error: err.message || "Delete failed" });
    }
  };

  handleMarkCompleted = async (task) => {
    if (!window.confirm("Mark this task as completed?")) return;
    const storeId = this.context?.storeId;
    if (!storeId) return this.setState({ error: "StoreId missing." });

    const dto = {
      status: "Completed",
      markCompleted: true,
    };

    try {
      const res = await this.fetchWithAuth(
        `${process.env.REACT_APP_API}HotelHousekeepingTask/${task.housekeepingTaskId}?storeId=${storeId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(dto),
        }
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => res.statusText);
        throw new Error(`${res.status} ${txt}`);
      }
      await this.loadAllData();
    } catch (err) {
      console.error("Error marking completed", err);
      this.setState({ error: err.message || "Failed to mark completed" });
    }
  };

  render() {
    const {
      tasks,
      rooms,
      showModal,
      newItem,
      editItem,
      loading,
      saving,
      error,
    } = this.state;

    return (
      <div className="container mt-4">
        <h4>Housekeeping Tasks</h4>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="mb-3">
          <Button variant="primary" onClick={() => this.handleShowModal()}>
            Add Task
          </Button>{" "}
          <Button
            variant="outline-secondary"
            onClick={() => this.loadAllData()}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" /> Refreshing
              </>
            ) : (
              "Refresh"
            )}
          </Button>
        </div>

        <Table bordered hover responsive>
          <thead className="table-light">
            <tr>
              <th>Room</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Created</th>
              <th>Completed</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center">
                  No tasks found
                </td>
              </tr>
            )}
            {tasks.map((t) => (
              <tr key={t.housekeepingTaskId}>
                <td>{t.room?.roomNumber || "—"}</td>
                <td>
                  {t.status === "Completed" ? (
                    <Badge bg="success">{t.status}</Badge>
                  ) : (
                    <Badge bg="warning">{t.status}</Badge>
                  )}
                </td>
                <td>
                  {t.priority === 1
                    ? "Low"
                    : t.priority === 2
                    ? "Medium"
                    : "High"}
                </td>
                <td>{t.createdAt ? t.createdAt.split("T")[0] : "—"}</td>
                <td>{t.completedAt ? t.completedAt.split("T")[0] : "—"}</td>
                <td>{t.notes || "—"}</td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => this.handleShowModal(t)}
                  >
                    Edit
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => this.handleDelete(t.housekeepingTaskId)}
                  >
                    Delete
                  </Button>{" "}
                  {t.status !== "Completed" && (
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => this.handleMarkCompleted(t)}
                    >
                      Mark Done
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Modal */}
        <Modal show={showModal} onHide={this.handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editItem ? "Edit Task" : "Add Task"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Room</Form.Label>
                <Form.Select
                  name="roomId"
                  value={newItem.roomId}
                  onChange={this.handleChange}
                >
                  <option value="">-- Select --</option>
                  {rooms.map((r) => (
                    <option key={r.roomId} value={r.roomId}>
                      {r.roomNumber}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={newItem.status}
                      onChange={this.handleChange}
                    >
                      <option value="Dirty">Dirty</option>
                      <option value="Cleaning">Cleaning</option>
                      <option value="Inspection">Inspection</option>
                      <option value="Completed">Completed</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      name="priority"
                      value={newItem.priority}
                      onChange={this.handleChange}
                    >
                      <option value={1}>Low</option>
                      <option value={2}>Medium</option>
                      <option value={3}>High</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="notes"
                  value={newItem.notes}
                  onChange={this.handleChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => this.handleSave()}
              disabled={saving}
            >
              {saving ? "Saving..." : editItem ? "Update" : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
